import moment from 'moment'
import { getField, updateField } from 'vuex-map-fields'

import * as mutation from '@/store/mutation-types'

const state = {
  offer: {},
  hotel: {},
  user: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    apartmentNumber: '',
    city: '',
    zipCode: '',
    gender: '',
    birthday: moment(0),
  },
  participants: [],
  additionalServices: [],
  selectedServices: [],
  rodoStatements: [],
  paymentMethod: '',
  sessionExpired: false,
}

const getters = {
  // Used for form multirows with the same fields
  getField,
  getOffer: state => state.offer,
  getHotel: state => state.hotel,
  getOfferTotalPrice: state => state.offer.TotalPrice,
  getUser: state => state.user,
  getPaymentMethod: state => state.paymentMethod,
  getAdditionalServices: state => state.additionalServices,
  getSelectedServices: state => state.selectedServices,
  getRodoStatements: state => state.rodoStatements,
  getSessionExpired: state => state.sessionExpired,
}

const actions = {
  [mutation.SET_OFFER] ({ commit }, offer) {
    commit(mutation.SET_OFFER, offer)
  },
  [mutation.SET_USER_FIELD] ({ commit }, data) {
    commit(mutation.SET_USER_FIELD, data)
  },
  [mutation.SET_PARTICIPANTS] ({ commit }, data) {
    commit(mutation.SET_PARTICIPANTS, data)
  },
  [mutation.SET_HOTEL] ({ commit }, data) {
    commit(mutation.SET_HOTEL, data)
  },
  [mutation.SET_FIRST_PARTICIPANT_FIELD] ({ commit }, data) {
    commit(mutation.SET_FIRST_PARTICIPANT_FIELD, data)
  },
  /**
   * The need of returning a promise comes from requiremet
   * to validate fields that are mutated in this action after mutation.
   */
  [mutation.FILL_FIRST_PARTICIPANT_FIELDS] ({ commit }, data) {
    return new Promise(resolve => {
      commit(mutation.FILL_FIRST_PARTICIPANT_FIELDS, data)
      resolve()
    })
  },
  [mutation.SET_SELECTED_SERVICE] ({ commit }, data) {
    commit(mutation.SET_SELECTED_SERVICE, data)
  },
  [mutation.SET_ADDITIONAL_SERVICES] ({ commit }, data) {
    commit(mutation.SET_ADDITIONAL_SERVICES, data)
  },
  [mutation.UPDATE_ADDITIONAL_SERVICES] ({ commit }, data) {
    commit(mutation.UPDATE_ADDITIONAL_SERVICES, data)
  },
  [mutation.SET_PAYMENT_METHOD] ({ commit }, data) {
    commit(mutation.SET_PAYMENT_METHOD, data)
  },
  [mutation.SET_RODO_STATEMENTS] ({ commit }, data) {
    commit(mutation.SET_RODO_STATEMENTS, data)
  },
  [mutation.SET_RODO_STATEMENT] ({ commit }, data) {
    commit(mutation.SET_RODO_STATEMENT, data)
  },
  [mutation.SET_SESSION_EXPIRED] ({ commit }, data) {
    commit(mutation.SET_SESSION_EXPIRED, data)
  },
}

const mutations = {
  // Used for form multirows with the same fields
  updateField,

  /**
   * @param {Object} payload offer data
   */
  [mutation.SET_OFFER] (state, payload) {
    state.offer = payload
  },

  /**
   * @param {Object} payload { field, value } of user form
   */
  [mutation.SET_USER_FIELD] (state, payload) {
    state.user[payload.field] = payload.value
  },

  /**
   * @param {Array} payload fields to build initial participants list.
   */
  [mutation.SET_PARTICIPANTS] (state, payload) {
    state.participants = payload
  },

  /**
   * @param {Array} payload fields to build initial hotel data.
   */
  [mutation.SET_HOTEL] (state, payload) {
    state.hotel = payload
  },

  /**
   * Fill field dynamically on user type.
   * This operation comes from business requirements.
   * @param {Object} payload { field, value } of user form
   *
   */
  [mutation.SET_FIRST_PARTICIPANT_FIELD] (state, payload) {
    if (state.participants[0].hasOwnProperty(payload.field)) {
      state.participants[0][payload.field] = payload.value
    }
  },

  /**
   * Fill all first participant fields on action.
   * This operation comes from business requirements.
   * @param {Object} payload { fields, values }
   */
  [mutation.FILL_FIRST_PARTICIPANT_FIELDS] (state, payload) {
    Object.keys(payload).forEach(key => {
      state.participants[0][key] = payload[key]
    })
  },

  /**
   * Set selectedServices array of object that are send
   * to backend to recalculate offer.
   * @param {Array} payload with array of objects
   */
  [mutation.SET_SELECTED_SERVICE] (state, payload) {
    function add (data) {
      return {
        type: data.ServiceTypeCode,
        code: data.MerlinCode,
        adults: data.selectedAdults,
        children: data.selectedChildren,
        infants: data.selectedInfants,
        selectedIDs: data.selectedIDs,
        isChecked: true,
      }
    }

    function update (selected, newData) {
      selected.adults = newData.selectedAdults
      selected.children = newData.selectedChildren
      selected.infants = newData.selectedInfants
      selected.selectedIDs = newData.selectedIDs
    }

    function updateSelected (service) {
      if (state.selectedServices.length) {
        const existing = state.selectedServices.find(selected => selected.code == service.MerlinCode)

        if (existing) update(existing, service)
        else state.selectedServices.push(add(service))
      } else {
        state.selectedServices.push(add(service))
      }
    }

    function removeSelected (service) {
      state.selectedServices = state.selectedServices.filter(selected => selected.code != service.MerlinCode)
    }

    payload.forEach(service => {
      if (service.selected) updateSelected(service)
      else removeSelected(service)
    })
  },

  /**
   * Set it once on component create.
   * @param {Object} payload with additional service
   */
  [mutation.SET_ADDITIONAL_SERVICES] (state, payload) {
    state.additionalServices.push(payload)
  },

  /**
   * Find service in 'state.additionalServices' which was selected
   * by user and set specific for UI properties with data
   * based on payload array with selected services.
   * @param {Array} payload with array of selected services by user
   */
  [mutation.UPDATE_ADDITIONAL_SERVICES] (state, payload) {
    payload.forEach(updatedService => {
      const service = state.additionalServices
        .find(g => g.code == updatedService.ServiceTypeCode).services
        .find(service => service.MerlinCode == updatedService.MerlinCode)

      service.selected = updatedService.selected
      service.selectedAdults = updatedService.selectedAdults
      service.selectedChildren = updatedService.selectedChildren
      service.selectedInfants = updatedService.selectedInfants
      service.selectedPeople = updatedService.selectedPeople
      service.selectedTotalPrice = updatedService.selectedTotalPrice
      service.selectedIDs = updatedService.selectedIDs
      service.options.forEach((option, i) => { option.selected = updatedService.options[i].selected })
    })
  },

  /**
   * @param {*} state
   * @param {*} payload { paymentMethod }
   */
  [mutation.SET_PAYMENT_METHOD] (state, payload) {
    state.paymentMethod = payload
  },

  /**
   * @param {Array} payload with rodo statements on component mount
   */
  [mutation.SET_RODO_STATEMENTS] (state, payload) {
    state.rodoStatements = payload.map(statement => ({
      value: statement.selected,
    }))
  },

  /**
   * @param {Object} payload with rodo statement ID and value on state change
   */
  [mutation.SET_RODO_STATEMENT] (state, { id, value }) {
    state.rodoStatements[id].value = value
  },

  /**
   * @param {Boolean} payload with boolean whether 'fake' session expired or not
   */
  [mutation.SET_SESSION_EXPIRED] (state, payload) {
    state.sessionExpired = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}


// WEBPACK FOOTER //
// ./src/store/modules/booking.js